import MenuIcon from '@mui/icons-material/Menu';
import { Box, IconButton, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { DrawerProps } from './Drawer.props';
import { DrawerContent } from './DrawerContent';
import { DrawerFooter } from './DrawerFooter';
import { MiniDrawer } from './MiniDrawer';
import { MobileDrawer } from './MobileDrawer';
import { ScreenSize } from '../../../@types';
import { toggleSidebar } from '../../../store/sidebarStore';
import { isMobileDevice } from '../../../utils/is-mobile-device';
import { SidebarPhonePanel } from '../SidebarPhonePanel';

const DRAWER_WIDTH = 220;
const MINI_DRAWER_WIDTH = 50;
const HIDE_MENU_KEY = 'hide_menu';
const TRANSITION =
  'width 0.25s cubic-bezier(0.4,0,0.2,1), min-width 0.25s cubic-bezier(0.4,0,0.2,1)';

export const Drawer: React.FC<DrawerProps> = ({ menuData, isOutsideClick, handleOutsideClick }) => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const hideMenu = searchParams.get(HIDE_MENU_KEY) || localStorage.getItem(HIDE_MENU_KEY);

  const [isHidden, setIsHidden] = useState<boolean>(hideMenu === 'true');
  const [isMenuItemClicked, setIsMenuItemClicked] = useState<boolean>(false);
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  // Treat mobile-UA devices as mobile regardless of viewport width — wide
  // phones can exceed the 600px breakpoint but should still get the slide-out
  // drawer (which is also where the "back to mobile services" toggle lives).
  const isMobileViewport = useMediaQuery(ScreenSize.MOBILE);
  const isMobileView = isMobileViewport || isMobileDevice();

  useEffect(() => {
    if (isMobileView && isOutsideClick) {
      setMobileOpen(false);
      handleOutsideClick(false);
    }
  }, [isOutsideClick]);

  useEffect(() => {
    if (isHidden) {
      localStorage.setItem(HIDE_MENU_KEY, 'true');
    } else {
      localStorage.removeItem(HIDE_MENU_KEY);
    }
  }, [isHidden]);

  useEffect(() => {
    if (!isMenuItemClicked) return;
    // Mobile drawer is an overlay — auto-close on navigation. Desktop sidebar is
    // persistent and must preserve the user's expanded/collapsed preference.
    if (isMobileView) {
      setMobileOpen(false);
    }
    setIsMenuItemClicked(false);
  }, [isMenuItemClicked]);

  const toggleHiddenState = () => {
    setIsHidden(!isHidden);
    dispatch(toggleSidebar());
    setIsMenuItemClicked(false);
    handleOutsideClick(false);
  };

  const toggleDrawer = (newOpen: boolean) => () => {
    setIsHidden(newOpen);
  };

  if (isMobileView) {
    return (
      <MobileDrawer
        menuData={menuData}
        open={mobileOpen}
        onOpen={() => setMobileOpen(true)}
        onClose={() => setMobileOpen(false)}
        onMenuItemClick={setIsMenuItemClicked}
        width={DRAWER_WIDTH}
      />
    );
  }

  const currentWidth = isHidden ? MINI_DRAWER_WIDTH : DRAWER_WIDTH;

  return (
    <Box
      component="nav"
      data-testid="sidebar-drawer"
      sx={{
        width: currentWidth,
        minWidth: currentWidth,
        height: '100vh',
        position: 'sticky',
        top: 0,
        bgcolor: 'v4.sidebar',
        display: 'flex',
        flexDirection: 'column',
        transition: TRANSITION,
        overflow: 'hidden',
        flexShrink: 0,
        '@media print': { display: 'none' },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: isHidden ? 0 : '14px',
          py: '12px',
          flexDirection: isHidden ? 'column' : 'row',
          gap: isHidden ? 0.5 : 0,
          transition: 'padding 0.25s ease',
          position: 'relative',
        }}
      >
        {isHidden ? (
          <Box
            component="img"
            src="/images/leftMenu/Amtek_Logo_Phoenix_Single_White@0.5x.png"
            alt="Company logo"
            sx={{ height: 22 }}
          />
        ) : (
          <Box
            component="svg"
            viewBox="0 0 500 500"
            sx={{ height: 76, width: 'auto', flexShrink: 0, mr: isHidden ? 0 : 3 }}
            aria-label="Company logo"
          >
            <path
              fill="#fff"
              d="m 181.85753,293.64875 68.14246,32.25769 68.13077,-32.25769 -68.13077,-69.59329 z M 317.14794,147.9217 292.21463,103.75313 250.0117,29 v 0 0 0 l -49.4688,87.63514 -17.69084,31.28656 67.14793,68.58708 z M 500,471.7856 440.55085,366.48303 h -43.60697 l 41.92213,42.82305 11.19717,11.44288 v 0 z M 246.30271,220.22947 180.13759,152.71882 117.76337,263.22802 v 0 l 59.08645,27.97537 z m 76.88258,70.97392 59.07474,-27.96367 -62.40933,-110.57941 -66.15341,67.56916 z M 103.06782,366.57663 H 59.425749 L 0,471.7856 29.730425,441.36486 79.725745,390.30481 Z m 288.69284,-5.28852 h 45.80662 l -52.65129,-93.39168 -57.79941,27.35527 z m -69.722,-63.64956 -65.87261,31.19296 68.52857,32.4566 h 59.67145 z M 172.90682,295.2985 115.13081,267.93153 62.409323,361.3349 h 45.830017 z m 162.96157,71.31324 100.06085,47.35105 -46.40334,-47.39785 z M 187.63748,361.3349 h 124.71334 l -62.35083,-29.53151 z m -9.68784,-63.64955 -62.31571,63.64955 h 59.67145 l 68.52857,-32.45658 z M 95.755132,381.6349 64.936584,413.13207 64.070763,413.99789 164.1433,366.61174 h -53.6809 z"
            />
          </Box>
        )}
        <IconButton
          onClick={toggleHiddenState}
          size="small"
          aria-label={isHidden ? 'Expand sidebar' : 'Collapse sidebar'}
          sx={{
            color: '#94a3b8',
            ...(isHidden
              ? {}
              : {
                  position: 'absolute',
                  right: 14,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  border: '1px solid',
                  borderColor: '#334155',
                  borderRadius: '4px',
                  p: '2px',
                }),
            '&:hover': { color: '#fff' },
          }}
        >
          <MenuIcon sx={{ fontSize: isHidden ? 18 : 16 }} />
        </IconButton>
      </Box>

      {/* Nav content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
          scrollbarColor: 'transparent transparent',
          '&:hover': {
            scrollbarColor: 'rgba(148,163,184,0.3) transparent',
          },
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'transparent',
            borderRadius: 4,
          },
          '&:hover::-webkit-scrollbar-thumb': {
            background: 'rgba(148,163,184,0.3)',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(148,163,184,0.5)',
          },
        }}
      >
        {isHidden ? (
          <MiniDrawer menuData={menuData} />
        ) : (
          <DrawerContent menuData={menuData} handleMenuItemClick={setIsMenuItemClicked} />
        )}
      </Box>

      {/* Softphone panel — hidden when the user lacks the Connect permission. */}
      <SidebarPhonePanel isCollapsed={isHidden} />

      {/* Footer */}
      {!isHidden && <DrawerFooter toggleDrawer={toggleDrawer(true)} />}
    </Box>
  );
};
